import { Alert, Button, FormField, Tabs, toast } from "@gotts.ai/ui";
import { useEffect, useMemo, useState } from "react";
import { encodeAbiParameters, keccak256, parseUnits } from "viem";

import { AmountInput } from "../../components/AmountInput";
import { PcbCard } from "../../components/PcbCard";
import { TokenIcon } from "../../components/TokenIcon";
import { TokenSelector } from "../../components/TokenSelector";
import { useDeployment, useWallet } from "../../context";
import { useTransactionFlow } from "../../hooks/useTransactionFlow";
import { useTxFeedback } from "../../hooks/useTxFeedback";
import {
  MOCK_ERC20_ABI,
  POOL_MODIFY_LIQUIDITY_TEST_ABI,
  STATE_VIEW_ABI,
  V2_ROUTER_ABI,
  V3_FACTORY_ABI,
  V3_NFT_POSITION_MANAGER_ABI,
} from "../../lib/abis";
import {
  deadline,
  DEVENV_TOKEN_PRICES,
  getInitialSqrtPriceX96,
  liquidityFromAmounts,
  MAX_UINT256,
  sortTokens,
  TOKEN_DECIMALS,
  V3_FEE_TIERS,
  ZERO_ADDRESS,
} from "../../lib/constants";
import {
  formatTokenAmount,
  formatTokenDisplay,
  formatUsd,
} from "../../lib/format";

import { ConfirmationModal } from "./ConfirmationModal";
import { TransactionStepper } from "./TransactionStepper";
import { MAX_TICK, MIN_TICK, tickToPrice } from "./types";
import { useV2PoolPrice } from "./useV2PoolPrice";
import { useV4PoolState } from "./useV4PoolState";

import type { TokenBalance } from "../../hooks/useTokenBalances";
import type { StepDefinition } from "../../hooks/useTransactionFlow";
import type { TokenSymbol } from "../../lib/constants";
import type { V4PositionDescriptor } from "./types";
import type { Address, Hex } from "viem";

interface AddLiquidityFormProps {
  isConnected: boolean;
  onSuccess: () => void;
  balances: Record<string, TokenBalance>;
  balancesLoading?: boolean;
  refetchBalances: () => void;
  /** Pool ticks from V3 positions (for center-on-price in V3 mode) */
  poolTicks?: Record<string, number>;
  /** Called after a V4 position is successfully added, to register it for tracking. */
  onV4PositionAdded?: (desc: V4PositionDescriptor) => void;
}

export function AddLiquidityForm({
  isConnected,
  onSuccess,
  balances,
  balancesLoading,
  refetchBalances,
  poolTicks,
  onV4PositionAdded,
}: AddLiquidityFormProps) {
  const { deployment } = useDeployment();
  const { walletClient, address, publicClient, chain } = useWallet();
  const flow = useTransactionFlow();
  const { feedbackClass, onTxSuccess, onTxError } = useTxFeedback();

  // Form state
  const [addProtocol, setAddProtocol] = useState<"V2" | "V3" | "V4">("V2");
  const [tokenA, setTokenA] = useState<TokenSymbol>("WETH");
  const [tokenB, setTokenB] = useState<TokenSymbol>("USDC");
  const [amountA, setAmountA] = useState("");
  const [amountB, setAmountB] = useState("");
  const [v3Fee, setV3Fee] = useState("3000");
  const [tickLower, setTickLower] = useState("-887220");
  const [tickUpper, setTickUpper] = useState("887220");
  const [showConfirmation, setShowConfirmation] = useState(false);

  // V4 pool state
  const v4PoolState = useV4PoolState(tokenA, tokenB, addProtocol === "V4");

  // V2 pool price
  const v2PoolPrice = useV2PoolPrice(tokenA, tokenB, addProtocol === "V2");

  // Auto-center tick range when V4 pool state first loads
  const [hasAutoCentered, setHasAutoCentered] = useState(false);
  useEffect(() => {
    if (!v4PoolState || hasAutoCentered || addProtocol !== "V4") return;
    const spacing = 60;
    const centered = Math.round(v4PoolState.tick / spacing) * spacing;
    const lower = Math.max(-887220, centered - 60000);
    const upper = Math.min(887220, centered + 60000);
    setTickLower(String(lower));
    setTickUpper(String(upper));
    setHasAutoCentered(true);
  }, [v4PoolState, hasAutoCentered, addProtocol]);

  // Reset auto-center flag when switching away from V4 or changing tokens
  useEffect(() => {
    setHasAutoCentered(false);
  }, [tokenA, tokenB, addProtocol]);

  // V2 auto-ratio: when user enters amountA, compute amountB from pool price
  const [v2AutoRatio, setV2AutoRatio] = useState(true);
  useEffect(() => {
    if (addProtocol !== "V2" || !v2AutoRatio || !v2PoolPrice) return;
    // Only auto-calculate if user typed amountA and price exists
    if (amountA && v2PoolPrice.price > 0) {
      const computed = (parseFloat(amountA) * v2PoolPrice.price).toFixed(6);
      setAmountB(computed);
    }
  }, [amountA, v2PoolPrice?.price, v2AutoRatio, addProtocol]);

  // Position type: whether current tick is in-range, below, or above the tick range
  const positionType = useMemo(() => {
    if (addProtocol !== "V4" || !v4PoolState) return "both" as const;
    const tl = parseInt(tickLower);
    const tu = parseInt(tickUpper);
    if (isNaN(tl) || isNaN(tu)) return "both" as const;
    if (v4PoolState.tick < tl) return "token0" as const;
    if (v4PoolState.tick >= tu) return "token1" as const;
    return "both" as const;
  }, [addProtocol, v4PoolState, tickLower, tickUpper]);

  // Token address helpers (must be before poolPriceDisplay which calls getTokenAddress)
  const tokenAddresses = useMemo(() => {
    if (!deployment?.tokens) return {} as Record<string, Address>;
    return deployment.tokens as unknown as Record<string, Address>;
  }, [deployment]);

  function getTokenAddress(symbol: TokenSymbol): Address | null {
    const key = symbol.toLowerCase();
    return tokenAddresses[key] ?? null;
  }

  // Human-readable price from sqrtPriceX96 (V4)
  const poolPriceDisplay = useMemo(() => {
    if (!v4PoolState || addProtocol !== "V4") return null;
    const sqrtPrice = Number(v4PoolState.sqrtPriceX96) / 2 ** 96;
    const price = sqrtPrice * sqrtPrice;
    if (price === 0) return null;
    const decimalsA = TOKEN_DECIMALS[tokenA] ?? 18;
    const decimalsB = TOKEN_DECIMALS[tokenB] ?? 18;
    const adjustedPrice = price * 10 ** (decimalsA - decimalsB);
    const addrA = getTokenAddress(tokenA);
    const addrB = getTokenAddress(tokenB);
    if (!addrA || !addrB) return null;
    const aIsToken0 = addrA.toLowerCase() < addrB.toLowerCase();
    if (aIsToken0) {
      return `1 ${tokenA} = ${adjustedPrice.toFixed(2)} ${tokenB}`;
    }
    const inverse = adjustedPrice > 0 ? 1 / adjustedPrice : 0;
    return `1 ${tokenA} = ${inverse.toFixed(2)} ${tokenB}`;
  }, [v4PoolState, addProtocol, tokenA, tokenB, deployment]);

  // Tick-to-price labels for V3/V4
  const decimalsA = TOKEN_DECIMALS[tokenA] ?? 18;
  const decimalsB = TOKEN_DECIMALS[tokenB] ?? 18;
  const addrA = getTokenAddress(tokenA);
  const addrB = getTokenAddress(tokenB);
  const aIsToken0 =
    addrA && addrB ? addrA.toLowerCase() < addrB.toLowerCase() : true;
  const dec0 = aIsToken0 ? decimalsA : decimalsB;
  const dec1 = aIsToken0 ? decimalsB : decimalsA;
  const sym0 = aIsToken0 ? tokenA : tokenB;
  const sym1 = aIsToken0 ? tokenB : tokenA;

  const tickLowerPrice = useMemo(() => {
    const tl = parseInt(tickLower);
    if (isNaN(tl)) return null;
    if (tl <= MIN_TICK) return "Min: 0 (Full Range)";
    return `\u2248 ${tickToPrice(tl, dec0, dec1)} ${sym1}/${sym0}`;
  }, [tickLower, dec0, dec1, sym0, sym1]);

  const tickUpperPrice = useMemo(() => {
    const tu = parseInt(tickUpper);
    if (isNaN(tu)) return null;
    if (tu >= MAX_TICK) return "Max: \u221e (Full Range)";
    return `\u2248 ${tickToPrice(tu, dec0, dec1)} ${sym1}/${sym0}`;
  }, [tickUpper, dec0, dec1, sym0, sym1]);

  // V3 pool tick for center-on-price
  const v3PoolTick = useMemo(() => {
    if (addProtocol !== "V3" || !poolTicks) return undefined;
    const addrTokenA = getTokenAddress(tokenA);
    const addrTokenB = getTokenAddress(tokenB);
    if (!addrTokenA || !addrTokenB) return undefined;
    const [t0, t1] = sortTokens(addrTokenA, addrTokenB);
    const fee = parseInt(v3Fee);
    const key = `${t0}:${t1}:${String(fee)}`;
    return poolTicks[key];
  }, [addProtocol, tokenA, tokenB, v3Fee, poolTicks, deployment]);

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------
  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (tokenA === tokenB) {
      errors.tokens = "Base and Quote tokens must be different.";
    }
    const numA = parseFloat(amountA);
    const numB = parseFloat(amountB);

    // Check amounts
    if (amountA && numA <= 0) {
      errors.amountA = "Amount must be greater than 0.";
    }
    if (amountB && numB <= 0) {
      errors.amountB = "Amount must be greater than 0.";
    }

    // Check balance
    if (amountA && numA > 0 && balances[tokenA]) {
      const bal =
        Number(balances[tokenA].raw) / 10 ** balances[tokenA].decimals;
      if (numA > bal) errors.amountA = `Insufficient ${tokenA} balance.`;
    }
    if (amountB && numB > 0 && balances[tokenB]) {
      const bal =
        Number(balances[tokenB].raw) / 10 ** balances[tokenB].decimals;
      if (numB > bal) errors.amountB = `Insufficient ${tokenB} balance.`;
    }

    // V3/V4 tick validation
    if (addProtocol === "V3" || addProtocol === "V4") {
      const tl = parseInt(tickLower);
      const tu = parseInt(tickUpper);
      if (!isNaN(tl) && !isNaN(tu) && tl >= tu) {
        errors.tickRange = "Tick Lower must be less than Tick Upper.";
      }
    }

    return errors;
  }, [
    tokenA,
    tokenB,
    amountA,
    amountB,
    balances,
    addProtocol,
    tickLower,
    tickUpper,
  ]);

  const isFormValid = (() => {
    if (Object.keys(validationErrors).length > 0) return false;
    if (!isConnected) return false;
    const hasA = !!amountA && parseFloat(amountA) > 0;
    const hasB = !!amountB && parseFloat(amountB) > 0;
    // V4 out-of-range positions only need one token
    if (addProtocol === "V4" && v4PoolState) {
      if (positionType === "token0") return hasA;
      if (positionType === "token1") return hasB;
    }
    return hasA && hasB;
  })();

  // ---------------------------------------------------------------------------
  // Execute
  // ---------------------------------------------------------------------------
  async function handleAddLiquidity() {
    if (!walletClient || !address || !deployment?.tokens) return;

    const tokenAAddr = getTokenAddress(tokenA);
    const tokenBAddr = getTokenAddress(tokenB);
    if (!tokenAAddr || !tokenBAddr) return;

    const parsedAmountA = parseUnits(amountA, decimalsA);
    const parsedAmountB = parseUnits(amountB, decimalsB);

    const steps: StepDefinition[] = [];

    if (addProtocol === "V2") {
      const router = deployment.v2.router02;
      const wethAddr = deployment.tokens.weth;
      const isWethPairA = tokenAAddr.toLowerCase() === wethAddr.toLowerCase();
      const isWethPairB = tokenBAddr.toLowerCase() === wethAddr.toLowerCase();
      const isWethPair = isWethPairA || isWethPairB;

      if (isWethPair) {
        const otherToken = isWethPairA ? tokenBAddr : tokenAAddr;
        const otherAmount = isWethPairA ? parsedAmountB : parsedAmountA;
        const ethAmount = isWethPairA ? parsedAmountA : parsedAmountB;

        steps.push({
          label: `Approve ${isWethPairA ? tokenB : tokenA}`,
          execute: async () =>
            walletClient.writeContract({
              chain,
              account: address,
              address: otherToken,
              abi: MOCK_ERC20_ABI,
              functionName: "approve",
              args: [router, MAX_UINT256],
            }),
          skip: async () => {
            const allowance = await publicClient.readContract({
              address: otherToken,
              abi: MOCK_ERC20_ABI,
              functionName: "allowance",
              args: [address, router],
            });
            return allowance >= otherAmount;
          },
        });

        steps.push({
          label: "Add Liquidity",
          execute: async () =>
            walletClient.writeContract({
              chain,
              account: address,
              address: router,
              abi: V2_ROUTER_ABI,
              functionName: "addLiquidityETH",
              args: [otherToken, otherAmount, 0n, 0n, address, deadline()],
              value: ethAmount,
            }),
        });
      } else {
        steps.push({
          label: `Approve ${tokenA}`,
          execute: async () =>
            walletClient.writeContract({
              chain,
              account: address,
              address: tokenAAddr,
              abi: MOCK_ERC20_ABI,
              functionName: "approve",
              args: [router, MAX_UINT256],
            }),
          skip: async () => {
            const allowance = await publicClient.readContract({
              address: tokenAAddr,
              abi: MOCK_ERC20_ABI,
              functionName: "allowance",
              args: [address, router],
            });
            return allowance >= parsedAmountA;
          },
        });

        steps.push({
          label: `Approve ${tokenB}`,
          execute: async () =>
            walletClient.writeContract({
              chain,
              account: address,
              address: tokenBAddr,
              abi: MOCK_ERC20_ABI,
              functionName: "approve",
              args: [router, MAX_UINT256],
            }),
          skip: async () => {
            const allowance = await publicClient.readContract({
              address: tokenBAddr,
              abi: MOCK_ERC20_ABI,
              functionName: "allowance",
              args: [address, router],
            });
            return allowance >= parsedAmountB;
          },
        });

        steps.push({
          label: "Add Liquidity",
          execute: async () =>
            walletClient.writeContract({
              chain,
              account: address,
              address: router,
              abi: V2_ROUTER_ABI,
              functionName: "addLiquidity",
              args: [
                tokenAAddr,
                tokenBAddr,
                parsedAmountA,
                parsedAmountB,
                0n,
                0n,
                address,
                deadline(),
              ],
            }),
        });
      }
    } else if (addProtocol === "V3") {
      const npm = deployment.v3.nonfungiblePositionManager;
      const [token0, token1] = sortTokens(tokenAAddr, tokenBAddr);
      const token0IsA = token0.toLowerCase() === tokenAAddr.toLowerCase();
      const amount0 = token0IsA ? parsedAmountA : parsedAmountB;
      const amount1 = token0IsA ? parsedAmountB : parsedAmountA;

      steps.push({
        label: `Approve ${token0IsA ? tokenA : tokenB}`,
        execute: async () =>
          walletClient.writeContract({
            chain,
            account: address,
            address: token0,
            abi: MOCK_ERC20_ABI,
            functionName: "approve",
            args: [npm, MAX_UINT256],
          }),
        skip: async () => {
          const allowance = await publicClient.readContract({
            address: token0,
            abi: MOCK_ERC20_ABI,
            functionName: "allowance",
            args: [address, npm],
          });
          return allowance >= amount0;
        },
      });

      steps.push({
        label: `Approve ${token0IsA ? tokenB : tokenA}`,
        execute: async () =>
          walletClient.writeContract({
            chain,
            account: address,
            address: token1,
            abi: MOCK_ERC20_ABI,
            functionName: "approve",
            args: [npm, MAX_UINT256],
          }),
        skip: async () => {
          const allowance = await publicClient.readContract({
            address: token1,
            abi: MOCK_ERC20_ABI,
            functionName: "allowance",
            args: [address, npm],
          });
          return allowance >= amount1;
        },
      });

      steps.push({
        label: "Create Pool",
        execute: async () => {
          const sqrtPriceX96 = getInitialSqrtPriceX96(
            token0,
            token1,
            deployment.tokens as unknown as Record<string, Address>,
          );
          return walletClient.writeContract({
            chain,
            account: address,
            address: npm,
            abi: V3_NFT_POSITION_MANAGER_ABI,
            functionName: "createAndInitializePoolIfNecessary",
            args: [token0, token1, parseInt(v3Fee), sqrtPriceX96],
          });
        },
        skip: async () => {
          const pool = await publicClient.readContract({
            address: deployment.v3.factory,
            abi: V3_FACTORY_ABI,
            functionName: "getPool",
            args: [token0, token1, parseInt(v3Fee)],
          });
          return pool !== ZERO_ADDRESS;
        },
      });

      steps.push({
        label: "Mint Position",
        execute: async () =>
          walletClient.writeContract({
            chain,
            account: address,
            address: npm,
            abi: V3_NFT_POSITION_MANAGER_ABI,
            functionName: "mint",
            args: [
              {
                token0,
                token1,
                fee: parseInt(v3Fee),
                tickLower: parseInt(tickLower),
                tickUpper: parseInt(tickUpper),
                amount0Desired: amount0,
                amount1Desired: amount1,
                amount0Min: 0n,
                amount1Min: 0n,
                recipient: address,
                deadline: deadline(),
              },
            ],
          }),
      });
    } else {
      // V4
      const modLiq = deployment.v4.poolModifyLiquidityTest;
      const [currency0, currency1] = sortTokens(tokenAAddr, tokenBAddr);
      const currency0IsA = currency0.toLowerCase() === tokenAAddr.toLowerCase();
      const amount0 = currency0IsA ? parsedAmountA : parsedAmountB;
      const amount1 = currency0IsA ? parsedAmountB : parsedAmountA;
      const tl = parseInt(tickLower);
      const tu = parseInt(tickUpper);

      // Query pool state inline if not already loaded
      let poolState = v4PoolState;
      if (!poolState) {
        try {
          const poolId = keccak256(
            encodeAbiParameters(
              [
                { type: "address" },
                { type: "address" },
                { type: "uint24" },
                { type: "int24" },
                { type: "address" },
              ],
              [currency0, currency1, 3000, 60, ZERO_ADDRESS],
            ),
          );
          const result = (await publicClient.readContract({
            address: deployment.v4.stateView,
            abi: STATE_VIEW_ABI,
            functionName: "getSlot0",
            args: [poolId],
          })) as readonly [bigint, number, number, number];
          if (result[0] > 0n) {
            poolState = { sqrtPriceX96: result[0], tick: result[1] };
          }
        } catch {
          // Pool may not exist
        }
      }

      if (!poolState) {
        toast.error("V4 pool not initialized. Try WETH/USDC or WETH/DAI.");
        return;
      }

      const liquidityDelta = liquidityFromAmounts(
        poolState.sqrtPriceX96,
        poolState.tick,
        tl,
        tu,
        amount0,
        amount1,
      );

      if (liquidityDelta <= 0n) {
        toast.error(
          "Cannot compute liquidity from the given amounts. The pool price may be outside your tick range — try adjusting amounts or tick range.",
        );
        return;
      }

      steps.push({
        label: `Approve ${tokenA}`,
        execute: async () =>
          walletClient.writeContract({
            chain,
            account: address,
            address: tokenAAddr,
            abi: MOCK_ERC20_ABI,
            functionName: "approve",
            args: [modLiq, MAX_UINT256],
          }),
        skip: async () => {
          const allowance = await publicClient.readContract({
            address: tokenAAddr,
            abi: MOCK_ERC20_ABI,
            functionName: "allowance",
            args: [address, modLiq],
          });
          return allowance >= parsedAmountA;
        },
      });

      steps.push({
        label: `Approve ${tokenB}`,
        execute: async () =>
          walletClient.writeContract({
            chain,
            account: address,
            address: tokenBAddr,
            abi: MOCK_ERC20_ABI,
            functionName: "approve",
            args: [modLiq, MAX_UINT256],
          }),
        skip: async () => {
          const allowance = await publicClient.readContract({
            address: tokenBAddr,
            abi: MOCK_ERC20_ABI,
            functionName: "allowance",
            args: [address, modLiq],
          });
          return allowance >= parsedAmountB;
        },
      });

      steps.push({
        label: "Add V4 Liquidity",
        execute: async () =>
          walletClient.writeContract({
            chain,
            account: address,
            address: modLiq,
            abi: POOL_MODIFY_LIQUIDITY_TEST_ABI,
            functionName: "modifyLiquidity",
            args: [
              {
                currency0,
                currency1,
                fee: 3000,
                tickSpacing: 60,
                hooks: ZERO_ADDRESS,
              },
              {
                tickLower: tl,
                tickUpper: tu,
                liquidityDelta,
                salt: "0x0000000000000000000000000000000000000000000000000000000000000000" as Hex,
              },
              "0x" as Hex,
            ],
          }),
      });
    }

    const toastId = toast.loading(
      `Adding ${addProtocol} liquidity: ${formatTokenDisplay(amountA).display} ${tokenA} + ${formatTokenDisplay(amountB).display} ${tokenB}...`,
    );
    await flow.execute(
      steps,
      () => {
        toast.dismiss(toastId);
        toast.success(
          `Added ${addProtocol} liquidity: ${formatTokenDisplay(amountA).display} ${tokenA} + ${formatTokenDisplay(amountB).display} ${tokenB}`,
        );
        // Register V4 position for tracking
        if (addProtocol === "V4" && onV4PositionAdded) {
          const [c0, c1] = sortTokens(tokenAAddr, tokenBAddr);
          onV4PositionAdded({
            currency0: c0,
            currency1: c1,
            fee: 3000,
            tickSpacing: 60,
            hooks: ZERO_ADDRESS,
            tickLower: parseInt(tickLower),
            tickUpper: parseInt(tickUpper),
            salt: "0x0000000000000000000000000000000000000000000000000000000000000000" as Hex,
          });
        }
        // Clear form
        setAmountA("");
        setAmountB("");
        refetchBalances();
        onTxSuccess();
        onSuccess();
      },
      (_label, _err) => {
        onTxError();
      },
    );
    toast.dismiss(toastId);
  }

  const hasError = flow.steps.some((s) => s.status === "error");
  const errorStep = flow.steps.find((s) => s.status === "error");

  // Pool share estimate (V2 only)
  const { poolShareEstimate, poolTotalUsd } = useMemo(() => {
    if (addProtocol !== "V2" || !v2PoolPrice || v2PoolPrice.price === 0)
      return { poolShareEstimate: undefined, poolTotalUsd: undefined };
    const rSym0 = aIsToken0 ? tokenA : tokenB;
    const rSym1 = aIsToken0 ? tokenB : tokenA;
    const rDec0 = TOKEN_DECIMALS[rSym0] ?? 18;
    const rDec1 = TOKEN_DECIMALS[rSym1] ?? 18;
    const reserve0Usd =
      (Number(v2PoolPrice.reserve0) / 10 ** rDec0) *
      (DEVENV_TOKEN_PRICES[rSym0] ?? 0);
    const reserve1Usd =
      (Number(v2PoolPrice.reserve1) / 10 ** rDec1) *
      (DEVENV_TOKEN_PRICES[rSym1] ?? 0);
    const poolUsd = reserve0Usd + reserve1Usd;
    const depositUsd =
      (parseFloat(amountA) || 0) * (DEVENV_TOKEN_PRICES[tokenA] ?? 0) +
      (parseFloat(amountB) || 0) * (DEVENV_TOKEN_PRICES[tokenB] ?? 0);
    if (poolUsd + depositUsd === 0)
      return { poolShareEstimate: undefined, poolTotalUsd: undefined };
    const share = (depositUsd / (poolUsd + depositUsd)) * 100;
    return {
      poolShareEstimate: `${share.toFixed(2)}%`,
      poolTotalUsd: poolUsd,
    };
  }, [addProtocol, v2PoolPrice, amountA, amountB, tokenA, tokenB, aIsToken0]);

  // Pair preview text
  const pairPreview = tokenA !== tokenB ? `${tokenA}/${tokenB} pool` : null;

  // Balance-formatted map for TokenSelector dropdown
  const balancesFormatted = useMemo(() => {
    const map: Record<string, { formatted: string }> = {};
    for (const [sym, bal] of Object.entries(balances)) {
      map[sym] = {
        formatted: formatTokenAmount(bal.raw, bal.decimals).display,
      };
    }
    return map;
  }, [balances]);

  // Dynamic CTA label
  const ctaLabel = flow.isExecuting
    ? "Adding Liquidity..."
    : balancesLoading
      ? "Loading Balances..."
      : !isFormValid
        ? "Enter Amount"
        : "Add Liquidity";

  // Show ticks toggle for V3/V4
  const [showTicks, setShowTicks] = useState(false);

  return (
    <>
      <PcbCard
        label="LP-01"
        title="Add Liquidity"
        className={`w-full max-w-[420px] [&_.pcb-card-inner]:p-0 ${feedbackClass}`}
        cut={10}
      >
        <div id="add-liquidity-form" className="p-4">
          {!isConnected ? (
            <Alert variant="warning" title="Wallet Required">
              Connect a wallet to add liquidity.
            </Alert>
          ) : (
            <div>
              {/* Protocol tabs — no label wrapper */}
              <Tabs
                tabs={[
                  { label: "V2", value: "V2" },
                  { label: "V3", value: "V3" },
                  { label: "V4", value: "V4" },
                ]}
                value={addProtocol}
                onChange={(v) => {
                  setAddProtocol(v as "V2" | "V3" | "V4");
                }}
              />

              <div className="mt-4 space-y-5">
                {/* Token selectors */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Base token">
                      <TokenSelector
                        symbol={tokenA}
                        onChange={setTokenA}
                        testId="token-a-selector"
                        tokenAddresses={tokenAddresses}
                        balances={balancesFormatted}
                      />
                    </FormField>
                    <FormField label="Quote token">
                      <TokenSelector
                        symbol={tokenB}
                        onChange={setTokenB}
                        testId="token-b-selector"
                        tokenAddresses={tokenAddresses}
                        balances={balancesFormatted}
                      />
                    </FormField>
                  </div>

                  {/* Pair preview banner */}
                  {pairPreview && (
                    <div className="octa-md flex items-center gap-3 bg-[rgba(255,255,255,0.04)] px-4 py-3">
                      <div className="flex -space-x-2">
                        <TokenIcon symbol={tokenA} size={28} />
                        <TokenIcon symbol={tokenB} size={28} />
                      </div>
                      <div>
                        <span className="font-mono text-sm font-medium text-[var(--color-text)]">
                          {tokenA}/{tokenB}
                        </span>
                        {addProtocol === "V2" && (
                          <span className="ml-2 font-mono text-[10px] text-[var(--color-text-muted)]">
                            0.30% fee
                          </span>
                        )}
                        {addProtocol === "V3" && (
                          <span className="ml-2 font-mono text-[10px] text-[var(--color-text-muted)]">
                            {(parseInt(v3Fee) / 10000).toFixed(2)}% fee
                          </span>
                        )}
                        {addProtocol === "V4" && (
                          <span className="ml-2 font-mono text-[10px] text-[var(--color-text-muted)]">
                            0.30% fee
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* V2 pool price banner */}
                {addProtocol === "V2" &&
                  v2PoolPrice &&
                  v2PoolPrice.price > 0 && (
                    <div className="octa-md flex items-center justify-between bg-[rgba(255,255,255,0.04)] px-4 py-2.5 text-xs">
                      <span className="type-field-label">Pool Price</span>
                      <span className="text-text font-mono">
                        1 {tokenA} ={" "}
                        {v2PoolPrice.price.toFixed(
                          v2PoolPrice.price >= 1 ? 2 : 6,
                        )}{" "}
                        {tokenB}
                      </span>
                    </div>
                  )}

                {/* Amount inputs */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label={tokenA}>
                      <AmountInput
                        value={amountA}
                        onChange={setAmountA}
                        symbol={tokenA}
                        balance={balances[tokenA]?.raw}
                        decimals={balances[tokenA]?.decimals ?? decimalsA}
                        loading={balancesLoading}
                        testId="amount-a"
                      />
                      {validationErrors.amountA && (
                        <span className="mt-0.5 block text-[10px] text-[var(--color-error)]">
                          {validationErrors.amountA}
                        </span>
                      )}
                      <span className="mt-0.5 block text-[10px] text-[var(--color-text-muted)]">
                        {"\u2248"}{" "}
                        {amountA &&
                        parseFloat(amountA) > 0 &&
                        DEVENV_TOKEN_PRICES[tokenA]
                          ? formatUsd(
                              parseFloat(amountA) * DEVENV_TOKEN_PRICES[tokenA],
                            )
                          : "$0.00"}
                      </span>
                    </FormField>
                    <FormField
                      label={
                        addProtocol === "V2" && v2AutoRatio && v2PoolPrice
                          ? `${tokenB} (estimated)`
                          : tokenB
                      }
                    >
                      <AmountInput
                        value={amountB}
                        onChange={(v) => {
                          setAmountB(v);
                          if (addProtocol === "V2") setV2AutoRatio(false);
                        }}
                        symbol={tokenB}
                        balance={balances[tokenB]?.raw}
                        decimals={balances[tokenB]?.decimals ?? decimalsB}
                        loading={balancesLoading}
                        testId="amount-b"
                      />
                      {validationErrors.amountB && (
                        <span className="mt-0.5 block text-[10px] text-[var(--color-error)]">
                          {validationErrors.amountB}
                        </span>
                      )}
                      <span className="mt-0.5 block text-[10px] text-[var(--color-text-muted)]">
                        {"\u2248"}{" "}
                        {amountB &&
                        parseFloat(amountB) > 0 &&
                        DEVENV_TOKEN_PRICES[tokenB]
                          ? formatUsd(
                              parseFloat(amountB) * DEVENV_TOKEN_PRICES[tokenB],
                            )
                          : "$0.00"}
                      </span>
                    </FormField>
                  </div>

                  {/* V2 auto-ratio toggle */}
                  {addProtocol === "V2" && v2PoolPrice && (
                    <button
                      type="button"
                      className="text-[10px] text-[var(--color-text-muted)] underline underline-offset-2 hover:text-[var(--color-gold)]"
                      onClick={() => {
                        setV2AutoRatio((v) => !v);
                      }}
                    >
                      {v2AutoRatio ? "Unlock auto-ratio" : "Lock auto-ratio"}
                    </button>
                  )}
                </div>

                {/* V3-specific fields */}
                {addProtocol === "V3" && (
                  <div className="space-y-4">
                    <FormField label="Fee tier">
                      <div className="flex gap-2">
                        {V3_FEE_TIERS.map((tier) => {
                          const isActive = v3Fee === String(tier.value);
                          return (
                            <button
                              key={tier.value}
                              onClick={() => {
                                setV3Fee(String(tier.value));
                              }}
                              className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                                isActive
                                  ? "border-[var(--color-gold)] bg-[var(--color-gold)] font-semibold text-[var(--color-black)]"
                                  : "border-[var(--glass-border)] bg-transparent text-[var(--color-text-dim)] hover:border-[rgba(201,168,76,0.3)] hover:text-[var(--color-gold)]"
                              }`}
                            >
                              {(tier.value / 10000).toFixed(2)}%
                              <span className="block text-[10px] opacity-70">
                                {tier.value === 100
                                  ? "Stables"
                                  : tier.value === 500
                                    ? "Low"
                                    : tier.value === 3000
                                      ? "Standard"
                                      : "Exotic"}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </FormField>

                    {/* Price / Tick inputs with toggle */}
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <span className="type-field-label">
                          {showTicks ? "Tick Range" : "Price Range"}
                        </span>
                        <button
                          type="button"
                          className="text-[10px] text-[var(--color-text-muted)] underline underline-offset-2 hover:text-[var(--color-gold)]"
                          onClick={() => {
                            setShowTicks((v) => !v);
                          }}
                        >
                          {showTicks ? "Show prices" : "Show ticks"}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <FormField
                          label={showTicks ? "Tick lower" : "Lower bound"}
                        >
                          <Input
                            type="number"
                            placeholder={showTicks ? "-887220" : "0.00"}
                            value={tickLower}
                            onChange={(e) => {
                              setTickLower(e.target.value);
                            }}
                          />
                          {tickLowerPrice && (
                            <span className="mt-0.5 block text-[10px] text-[var(--color-text-muted)]">
                              {tickLowerPrice}
                            </span>
                          )}
                        </FormField>
                        <FormField
                          label={showTicks ? "Tick upper" : "Upper bound"}
                        >
                          <Input
                            type="number"
                            placeholder={showTicks ? "887220" : "0.00"}
                            value={tickUpper}
                            onChange={(e) => {
                              setTickUpper(e.target.value);
                            }}
                          />
                          {tickUpperPrice && (
                            <span className="mt-0.5 block text-[10px] text-[var(--color-text-muted)]">
                              {tickUpperPrice}
                            </span>
                          )}
                        </FormField>
                      </div>
                    </div>

                    {validationErrors.tickRange && (
                      <Alert
                        variant="error"
                        title="Invalid Range"
                        className="mb-3"
                      >
                        {validationErrors.tickRange}
                      </Alert>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setTickLower("-887220");
                          setTickUpper("887220");
                        }}
                      >
                        Set Full Range
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={v3PoolTick === undefined}
                        onClick={() => {
                          if (v3PoolTick === undefined) return;
                          const spacing =
                            V3_FEE_TIERS.find((t) => String(t.value) === v3Fee)
                              ?.tickSpacing ?? 60;
                          const centered =
                            Math.round(v3PoolTick / spacing) * spacing;
                          const lower = Math.max(
                            MIN_TICK,
                            centered - spacing * 1000,
                          );
                          const upper = Math.min(
                            MAX_TICK,
                            centered + spacing * 1000,
                          );
                          setTickLower(String(lower));
                          setTickUpper(String(upper));
                        }}
                      >
                        Center on Price
                      </Button>
                    </div>
                  </div>
                )}

                {/* V4-specific fields */}
                {addProtocol === "V4" && (
                  <div className="space-y-4">
                    {v4PoolState ? (
                      v4PoolState.sqrtPriceX96 === 0n ? (
                        <Alert variant="warning" title="Pool Not Initialized">
                          This V4 pool has sqrtPriceX96 = 0. Initialize the pool
                          first.
                        </Alert>
                      ) : (
                        <div className="octa-md grid grid-cols-2 gap-x-4 gap-y-1 bg-[rgba(255,255,255,0.04)] px-4 py-2.5 text-xs">
                          <div>
                            <span className="type-field-label">Pool Tick</span>
                            <span className="ml-2 font-mono">
                              {v4PoolState.tick.toLocaleString()}
                            </span>
                          </div>
                          <div>
                            <span className="type-field-label">Fee</span>
                            <span className="ml-2 font-mono">0.30%</span>
                          </div>
                          {poolPriceDisplay && (
                            <div className="col-span-2">
                              <span className="type-field-label">
                                Pool Price
                              </span>
                              <span className="ml-2 font-mono">
                                {poolPriceDisplay}
                              </span>
                            </div>
                          )}
                          <div>
                            <span className="type-field-label">
                              Tick Spacing
                            </span>
                            <span className="ml-2 font-mono">
                              60{" "}
                              <span className="text-[var(--color-text-muted)] opacity-70">
                                (0.30% fee tier)
                              </span>
                            </span>
                          </div>
                        </div>
                      )
                    ) : (
                      <p className="type-supporting text-xs">
                        V4 uses PoolModifyLiquidityTest. Seeded pools: WETH/USDC
                        and WETH/DAI (fee=3000, tickSpacing=60).
                      </p>
                    )}

                    {/* Price / Tick inputs with toggle */}
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <span className="type-field-label">
                          {showTicks ? "Tick Range" : "Price Range"}
                        </span>
                        <button
                          type="button"
                          className="text-[10px] text-[var(--color-text-muted)] underline underline-offset-2 hover:text-[var(--color-gold)]"
                          onClick={() => {
                            setShowTicks((v) => !v);
                          }}
                        >
                          {showTicks ? "Show prices" : "Show ticks"}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <FormField
                          label={showTicks ? "Tick lower" : "Lower bound"}
                        >
                          <Input
                            type="number"
                            placeholder={showTicks ? "-887220" : "0.00"}
                            value={tickLower}
                            onChange={(e) => {
                              setTickLower(e.target.value);
                            }}
                          />
                          {tickLowerPrice && (
                            <span className="mt-0.5 block text-[10px] text-[var(--color-text-muted)]">
                              {tickLowerPrice}
                            </span>
                          )}
                        </FormField>
                        <FormField
                          label={showTicks ? "Tick upper" : "Upper bound"}
                        >
                          <Input
                            type="number"
                            placeholder={showTicks ? "887220" : "0.00"}
                            value={tickUpper}
                            onChange={(e) => {
                              setTickUpper(e.target.value);
                            }}
                          />
                          {tickUpperPrice && (
                            <span className="mt-0.5 block text-[10px] text-[var(--color-text-muted)]">
                              {tickUpperPrice}
                            </span>
                          )}
                        </FormField>
                      </div>
                    </div>

                    {validationErrors.tickRange && (
                      <Alert variant="error" title="Invalid Range">
                        {validationErrors.tickRange}
                      </Alert>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setTickLower("-887220");
                          setTickUpper("887220");
                        }}
                      >
                        Set Full Range
                      </Button>
                      {v4PoolState && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const spacing = 60;
                            const centered =
                              Math.round(v4PoolState.tick / spacing) * spacing;
                            const lower = Math.max(-887220, centered - 60000);
                            const upper = Math.min(887220, centered + 60000);
                            setTickLower(String(lower));
                            setTickUpper(String(upper));
                          }}
                        >
                          Center on Price
                        </Button>
                      )}
                    </div>

                    {/* Position type indicator */}
                    {v4PoolState && (
                      <div
                        className={`octa-md px-4 py-2.5 text-xs ${
                          positionType === "both"
                            ? "bg-[var(--color-in-range-bg)] text-[var(--color-in-range)]"
                            : "bg-[var(--color-out-of-range-bg)] text-[var(--color-out-of-range)]"
                        }`}
                      >
                        {positionType === "both" &&
                          "\u2139 Both tokens needed \u2014 price is in range, you earn fees on both sides"}
                        {positionType === "token0" &&
                          `\u2139 Only ${aIsToken0 ? tokenA : tokenB} needed \u2014 price is below your range, you provide the cheaper token and earn fees when price rises`}
                        {positionType === "token1" &&
                          `\u2139 Only ${aIsToken0 ? tokenB : tokenA} needed \u2014 price is above your range, you provide the pricier token and earn fees when price falls`}
                      </div>
                    )}
                  </div>
                )}

                {/* Transaction Stepper */}
                {flow.steps.length > 0 && (
                  <TransactionStepper steps={flow.steps} />
                )}

                {/* Error */}
                {hasError && errorStep && (
                  <Alert variant="error" title="Transaction Failed">
                    {errorStep.error}
                  </Alert>
                )}

                {validationErrors.tokens && (
                  <Alert variant="error" title="Invalid pair">
                    {validationErrors.tokens}
                  </Alert>
                )}

                {/* CTA with spacing */}
                <div className="pt-2">
                  <Button
                    variant="gold"
                    size="sm"
                    onClick={() => {
                      setShowConfirmation(true);
                    }}
                    disabled={
                      !isFormValid || flow.isExecuting || balancesLoading
                    }
                    loading={flow.isExecuting || balancesLoading}
                    className="w-full"
                  >
                    {ctaLabel}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </PcbCard>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <ConfirmationModal
          protocol={addProtocol}
          tokenA={tokenA}
          tokenB={tokenB}
          amountA={amountA}
          amountB={amountB}
          tickLower={addProtocol !== "V2" ? tickLower : undefined}
          tickUpper={addProtocol !== "V2" ? tickUpper : undefined}
          tickLowerPrice={tickLowerPrice ?? undefined}
          tickUpperPrice={tickUpperPrice ?? undefined}
          feeTier={
            addProtocol === "V3"
              ? v3Fee
              : addProtocol === "V4"
                ? "3000"
                : undefined
          }
          poolShareEstimate={poolShareEstimate}
          poolTotalUsd={poolTotalUsd}
          onCancel={() => {
            setShowConfirmation(false);
          }}
          onConfirm={() => {
            setShowConfirmation(false);
            void handleAddLiquidity();
          }}
        />
      )}
    </>
  );
}

// Re-export Input locally for the form's tick inputs (avoids circular import from @gotts.ai/ui)
function Input(props: {
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <input
      className="octa-sm w-full bg-[rgba(14,14,14,0.8)] px-3 py-2.5 font-mono text-sm text-white placeholder:text-white/20 focus:outline-none"
      value={props.value}
      onChange={props.onChange}
      placeholder={props.placeholder}
      type={props.type}
      disabled={props.disabled}
    />
  );
}
